import React, { useEffect, useRef, useState } from 'react'
import type { AriaDialogProps } from '@react-aria/dialog'
import { useDialog } from '@react-aria/dialog'
import { Overlay, useModalOverlay } from 'react-aria'
import type { OverlayTriggerState } from 'react-stately'
import { useAtomValue } from 'jotai'
import { modalShowHeader } from '@store/atoms'
import clsx from 'clsx'
import { IconBase } from './svg/IconBase'

type ModalOverlayProps = AriaDialogProps & {
	title: string
	isOpen: boolean
	onClose: () => void
}

type ModalDialogProps = {
	children: React.ReactNode
	close: () => void
	goBack: (() => void) | null
} & AriaDialogProps

const ModalDialog: React.FC<ModalDialogProps> = ({
	children,
	close,
	goBack,
	...props
}) => {
	const ref = useRef<HTMLDivElement>(null)
	const { dialogProps } = useDialog(props, ref)

	const shouldShowCloseBtn = useAtomValue(modalShowHeader)

	useEffect(() => {
		ref.current?.focus()
	}, [])

	return (
		<div {...dialogProps} ref={ref} className='outline-none relative'>
			{shouldShowCloseBtn && (
				<>
					{goBack && (
						<button
							onClick={goBack}
							className='absolute top-4 left-4 p-2'
							aria-label='Go back'
						>
							<img src='/assets/images/Back.svg' alt='Go back' />
						</button>
					)}
					<div className='flex justify-between pt-6 pr-6'>
						<div></div>
						<button
							className='
                          group
                          w-11 h-11 md:w-11 md:h-11
                          flex items-center justify-center
                          rounded-full
                
                          text-(--green-default)
                          transition-all duration-300
                          hover:bg-(--green-default)
                          hover:text-white
                        '
							onClick={close}
							aria-label='Close modal'
						>
							<IconBase
								className='w-full h-full transition-transform duration-300'
								icon={'M6 6L18 18M6 18L18 6'}
							/>
						</button>
					</div>
					{/*   <button
            onClick={close}
            className="absolute top-4 right-4 p-2"
            aria-label="Close modal"
          >
            <img src="/assets/images/Close.svg" alt="Close" />
          </button> */}
				</>
			)}

			<div>{children}</div>
		</div>
	)
}

type ModalProps = {
	children: React.ReactNode
	goBack: (() => void) | null
	state: OverlayTriggerState
} & ModalOverlayProps

const Modal: React.FC<ModalProps> = ({ children, state, goBack, ...props }) => {
	const ref = useRef<HTMLDivElement>(null)
	const overRef = useRef<HTMLDivElement>(null)

	const { modalProps, underlayProps } = useModalOverlay(props, state, ref)
	const [scrolled] = useState(false)

	return (
		<Overlay>
			<div
				{...underlayProps}
				ref={overRef}
				className={clsx(
					'fixed inset-0  bg-(--background) bg-opacity-50 z-1000 flex items-center justify-center',
					scrolled ? 'pointer-events-auto' : 'pointer-events-auto'
				)}
			>
				<div
					{...modalProps}
					ref={ref}
					className='bg-white shadow-[0px_3px_10px_0px_#0000001A]
           max-h-[488px] md:min-h-[488px] md:min-w-[1076px] max-w-[360px] md:max-w-[1076px] relative '
				>
					<ModalDialog goBack={goBack} close={state.close}>
						{children}
					</ModalDialog>
				</div>
			</div>
		</Overlay>
	)
}

export default Modal
